import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  EmailField,
  FunctionField,
  TopToolbar,
  CreateButton,
  ExportButton,
  useRecordContext,
  TextInput,
  SelectInput,
  BooleanField,
  DateField,
} from 'react-admin';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Stack,
  Button,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import UploadIcon from '@mui/icons-material/Upload';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { PageWrapper } from '../../components/PageWrapper';
import { createEmptyStateComponent } from '../../components/createEmptyStateComponent';
import { TenantInvitationButton } from '../../components/TenantInvitationButton';
import { BulkUserUploadDialog } from './BulkUserUploadDialog';
import { PendingInvitationsTab } from './PendingInvitationsTab';

// Styled components for empty state
const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  width: '100%',
  padding: theme.spacing(4),
  textAlign: 'center',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 120,
  height: 120,
  borderRadius: '50%',
  backgroundColor: theme.palette.grey[100],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  border: `2px dashed ${theme.palette.grey[300]}`,
}));

// Custom empty state component with bulk upload button
const UserEmptyState = () => {
  const navigate = useNavigate();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  return (
    <>
      <Box sx={{ 
        width: '100%', 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh'
      }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            maxWidth: '600px',
            width: '100%',
            margin: 'auto',
          }}
        >
          <EmptyStateContainer>
            <IconContainer>
              <PersonAddIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            </IconContainer>
            
            <Typography 
              variant="h5" 
              component="h2" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                mb: 2,
              }}
            >
              No Users Yet
            </Typography>
            
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                maxWidth: 480,
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              Add users to your system to manage access and permissions. Users can be drivers, administrators, or other team members.
            </Typography>
            
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => navigate('/users/create')}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
              >
                Add User
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                startIcon={<UploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                }}
              >
                Bulk Upload
              </Button>
            </Stack>
          </EmptyStateContainer>
        </Paper>
      </Box>
      
      <BulkUserUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
      />
    </>
  );
};

const ListActions = () => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  return (
    <>
      <TopToolbar>
        <Button
          onClick={() => setUploadDialogOpen(true)}
          startIcon={<UploadIcon />}
          size="small"
        >
          Bulk Upload
        </Button>
        <TenantInvitationButton />
        <CreateButton />
        <ExportButton />
      </TopToolbar>
      <BulkUserUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
      />
    </>
  );
};

// User Avatar Field
const UserAvatarField = () => {
  const record = useRecordContext();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Avatar
        sx={{
          bgcolor: record?.driver ? 'primary.main' : 'grey.400',
          width: 32,
          height: 32
        }}
      >
        {record?.driver ? <LocalShippingIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
      </Avatar>
      <Box>
        <Typography variant="body2" fontWeight={500}>
          {record?.email}
        </Typography>
      </Box>
    </Box>
  );
};

// Driver Status Field
const DriverStatusField = () => {
  const record = useRecordContext();
  
  if (!record?.driver) {
    return (
      <Chip 
        label="Not a Driver" 
        size="small" 
        variant="outlined"
        color="default"
      />
    );
  }
  
  const profileState = record.driver.profileState;
  
  const getStatusConfig = (state: string) => {
    switch (state) {
      case 'COMPLETE':
        return { 
          label: 'Driver - Complete', 
          color: 'success' as const,
          icon: <CheckCircleIcon fontSize="small" />
        };
      case 'INCOMPLETE':
        return { 
          label: 'Driver - Incomplete', 
          color: 'warning' as const,
          icon: <WarningIcon fontSize="small" />
        };
      case 'PENDING':
        return { 
          label: 'Driver - Pending', 
          color: 'info' as const,
          icon: <ErrorIcon fontSize="small" />
        };
      default:
        return { 
          label: 'Driver', 
          color: 'primary' as const,
          icon: <LocalShippingIcon fontSize="small" />
        };
    }
  };
  
  const config = getStatusConfig(profileState);
  
  return (
    <Chip 
      label={config.label}
      color={config.color}
      size="small"
      icon={config.icon}
    />
  );
};

// Role Field
const RoleField = () => {
  const record = useRecordContext();
  const roles = record?.roles || [];
  const isSuperAdmin = record?.isSuperAdmin === true;
  const isInactive = record?.isActive === false;

  if (roles.length === 0 && !isSuperAdmin && !isInactive) return <Typography variant="body2" color="text.secondary">-</Typography>;

  return (
    <Stack direction="row" spacing={0.5}>
      {isInactive && (
        <Chip
          label="inactive"
          size="small"
          variant="filled"
          sx={{ bgcolor: 'grey.700', color: 'white' }}
        />
      )}
      {isSuperAdmin && (
        <Chip
          label="super admin"
          size="small"
          variant="filled"
          color="secondary"
        />
      )}
      {roles.map((role: string, index: number) => (
        <Chip 
          key={index}
          label={role} 
          size="small" 
          variant="outlined"
          color={role === 'admin' ? 'error' : 'default'}
        />
      ))}
    </Stack>
  );
};

// Verification Status Field
const VerificationField = () => {
  const record = useRecordContext();
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {record?.emailVerified ? (
        <>
          <VerifiedUserIcon color="success" fontSize="small" />
          <Typography variant="body2" color="success.main">Verified</Typography>
        </>
      ) : (
        <>
          <ErrorIcon color="action" fontSize="small" />
          <Typography variant="body2" color="text.secondary">Unverified</Typography>
        </>
      )}
    </Box>
  );
};

// License Status Field (for drivers)
const LicenseStatusField = () => {
  const record = useRecordContext();
  
  if (!record?.driver) {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }
  
  const activeLicenses = record.driver.licenses?.filter((l: any) => l.status === 'ACTIVE').length || 0;
  const totalLicenses = record.driver.licenses?.length || 0;
  
  if (totalLicenses === 0) {
    return <Typography variant="body2" color="text.secondary">No licenses</Typography>;
  }
  
  return (
    <Box>
      <Typography variant="body2">
        {activeLicenses} active / {totalLicenses} total
      </Typography>
    </Box>
  );
};

// Recent Activity Field
const RecentActivityField = () => {
  const record = useRecordContext();

  if (!record?.lastActivityAt) {
    return <Typography variant="body2" color="text.secondary">No activity</Typography>;
  }

  return (
    <Typography variant="body2">
      {new Date(record.lastActivityAt).toLocaleDateString()}
    </Typography>
  );
};

const userFilters = [
  <TextInput
    key="search"
    label="Search"
    source="q"
    alwaysOn
    sx={{
      minWidth: 250,
      '& .MuiOutlinedInput-root': {
        minWidth: 250,
        height: '56px',
      }
    }}
  />,
  <SelectInput
    key="driverStatus"
    label="Driver Status"
    source="hasDriver"
    choices={[
      { id: 'true', name: 'Drivers Only' },
      { id: 'false', name: 'Non-Drivers Only' },
    ]}
    allowEmpty
    emptyText="All Users"
    alwaysOn
    sx={{
      minWidth: 250,
      '& .MuiOutlinedInput-root': {
        minWidth: 250,
        height: '56px',
      }
    }}
  />,
];

const TeamMembersTab = () => (
  <List
    actions={<ListActions />}
    filters={userFilters}
    empty={<UserEmptyState />}
    sort={{ field: 'name', order: 'ASC' }}
    perPage={25}
  >
    <Datagrid
      rowClick="show"
      bulkActionButtons={false}
      sx={{
        '& .RaDatagrid-row': {
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        },
      }}
    >
      <TextField source="firstName" label="First Name" />
      <TextField source="lastName" label="Last Name" />
      <UserAvatarField label="Email" />
      <DriverStatusField label="Status" />
      <RoleField label="Roles" />
    </Datagrid>
  </List>
);

export const UserList = () => {
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <PageWrapper title="Team Members">
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Team Members" />
          <Tab label="Pending Invitations" />
        </Tabs>
      </Box>

      {currentTab === 0 && <TeamMembersTab />}
      {currentTab === 1 && <PendingInvitationsTab />}
    </PageWrapper>
  );
};